import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  Mail, Lock, Trash2, ShieldCheck, Bell, UserCheck, FileText,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';

/**
 * /settings — single consolidated page (no more /settings/privacy).
 *
 * Sections (all on one scrollable page):
 *   1. Account         — email, password change, delete account (placeholders)
 *   2. Privacy         — consent toggles that persist via PATCH /users/me/profile
 *   3. Notifications   — email preferences placeholder
 */
const SectionCard = ({ title, subtitle, icon: Icon, children, anchorId }) => (
  <section
    id={anchorId}
    className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-soft scroll-mt-24"
    data-testid={`settings-${anchorId}`}
  >
    <div className="flex items-center gap-2.5 mb-1">
      {Icon && <Icon className="h-4.5 w-4.5 text-amber-600" />}
      <h2 className="font-heading font-bold text-lg text-slate-900">{title}</h2>
    </div>
    {subtitle && <p className="text-sm text-slate-600 mb-4">{subtitle}</p>}
    <div className={`${subtitle ? '' : 'mt-3'} space-y-4`}>{children}</div>
  </section>
);

const Settings = () => {
  const { api } = useAuth();
  const { bundle, refetch } = useProfile();
  const u = bundle?.user || {};
  const location = useLocation();

  // Scroll to #account / #privacy / #notifications on arrival (used by the
  // /settings/privacy → /settings#privacy redirect and the section nav).
  useEffect(() => {
    const hash = (location.hash || '').replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  }, [location.hash]);

  const [toggles, setToggles] = useState({
    show_country_on_profile: u.show_country_on_profile !== false,
    show_to_roommates: !!u.show_to_roommates,
    allow_landlord_doc_requests: u.allow_landlord_doc_requests !== false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Keep toggles in sync when the ProfileContext bundle refreshes.
    if (u && Object.keys(u).length) {
      setToggles({
        show_country_on_profile: u.show_country_on_profile !== false,
        show_to_roommates: !!u.show_to_roommates,
        allow_landlord_doc_requests: u.allow_landlord_doc_requests !== false,
      });
    }
  }, [u.user_id, u.show_country_on_profile, u.show_to_roommates, u.allow_landlord_doc_requests]);

  const patchToggle = async (key, val) => {
    // Optimistic — flip immediately; revert on failure.
    setToggles((t) => ({ ...t, [key]: val }));
    setSaving(true);
    try {
      await api.patch('/users/me/profile', { [key]: val });
      if (refetch) await refetch();
    } catch (e) {
      setToggles((t) => ({ ...t, [key]: !val }));
      toast.error("Couldn't save that setting. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-12" data-testid="settings-page">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-5 sm:py-6 space-y-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-extrabold text-slate-900">
            Settings
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Account, privacy, and notifications all in one place.
          </p>
        </div>

        {/* Sticky section nav (desktop only — less ceremony on mobile) */}
        <div className="hidden md:flex gap-2 text-xs font-medium" data-testid="settings-section-nav">
          {[
            { href: '#account', label: 'Account' },
            { href: '#privacy', label: 'Privacy' },
            { href: '#notifications', label: 'Notifications' },
          ].map((s) => (
            <a
              key={s.href}
              href={s.href}
              className="px-3 py-1.5 bg-white border border-slate-200 rounded-full text-slate-700 hover:border-amber-300"
            >
              {s.label}
            </a>
          ))}
        </div>

        {/* Account */}
        <SectionCard title="Account" icon={Mail} anchorId="account">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900">Email</p>
              <p className="text-xs text-slate-500 truncate">{u.email || '—'}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled
              title="Email changes ship in Phase 2"
              data-testid="change-email-btn"
            >
              Change
            </Button>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5 text-slate-400" /> Password
              </p>
              <p className="text-xs text-slate-500">Change your sign-in password.</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled
              title="Password reset ships in Phase 2"
              data-testid="change-password-btn"
            >
              Change
            </Button>
          </div>
          <Separator />
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-rose-700 flex items-center gap-1.5">
                <Trash2 className="h-3.5 w-3.5" /> Delete account
              </p>
              <p className="text-xs text-slate-500">
                Removes your profile, saved listings, and alerts. This can't be undone.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled
              title="Account deletion ships in Phase 2"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              data-testid="delete-account-btn"
            >
              Delete
            </Button>
          </div>
        </SectionCard>

        {/* Privacy */}
        <SectionCard
          title="Privacy"
          icon={ShieldCheck}
          subtitle="Control what landlords and other students see about you."
          anchorId="privacy"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900">Show country of origin on profile</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Helps multilingual landlords connect. Hidden if you turn this off.
              </p>
            </div>
            <Switch
              checked={toggles.show_country_on_profile}
              onCheckedChange={(v) => patchToggle('show_country_on_profile', v)}
              disabled={saving}
              data-testid="toggle-show-country"
            />
          </div>
          <Separator />
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 inline-flex items-center gap-1.5">
                <UserCheck className="h-3.5 w-3.5 text-slate-400" /> Show my profile to potential roommates
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Turn on when roommate matching launches to receive matches.
              </p>
            </div>
            <Switch
              checked={toggles.show_to_roommates}
              onCheckedChange={(v) => patchToggle('show_to_roommates', v)}
              disabled={saving}
              data-testid="toggle-show-roommates"
            />
          </div>
          <Separator />
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 inline-flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5 text-slate-400" /> Allow landlords to request documents
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Landlords can ask for your acceptance letter or ID during an application.
              </p>
            </div>
            <Switch
              checked={toggles.allow_landlord_doc_requests}
              onCheckedChange={(v) => patchToggle('allow_landlord_doc_requests', v)}
              disabled={saving}
              data-testid="toggle-landlord-docs"
            />
          </div>
        </SectionCard>

        {/* Notifications */}
        <SectionCard
          title="Notifications"
          icon={Bell}
          subtitle="Pick how we reach you."
          anchorId="notifications"
        >
          <Card className="bg-amber-50 border-amber-200/60">
            <CardContent className="p-4 text-sm text-amber-900 leading-relaxed">
              Email preference controls are on the way. For now we send transactional emails only
              (new messages, application updates, account security).
            </CardContent>
          </Card>
          <Link
            to="/alerts"
            className="text-sm text-amber-700 hover:underline font-medium inline-block"
            data-testid="manage-alerts-link"
          >
            Manage search alerts →
          </Link>
        </SectionCard>
      </main>
    </div>
  );
};

export default Settings;
